import { createTheme } from '@mui/material/styles';

/**
 * MUI v7 theme, wired to the same values as `theme/tokens.css`.
 *
 * Without this, every MUI component renders in Roboto at MUI's default radii
 * while the rest of the app is Figtree — which is why table cells containing a
 * Tooltip or Chip used to show a different typeface from the cell beside them,
 * and why menus and dialogs looked untouched by the theme.
 *
 * The literals below are duplicated from tokens.css on purpose: MUI computes
 * palette variants (hover, light/dark, contrast text) in JS, where `var()`
 * cannot be resolved. Keep the two files in step.
 */
const NAVY = '#0b2345';
const NAVY_SOFT = '#12345f';
const GREEN = '#2f8b5b';
const RED = '#bd2f28';
const AMBER = '#92600a';
const SURFACE = '#ffffff';
const BORDER = '#d6dfea';
const TEXT = '#0a1c31';
const TEXT_DIM = '#4a5c73';
const FONT_UI =
  "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif";

const oatTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: NAVY, dark: '#061731', light: NAVY_SOFT, contrastText: '#ffffff' },
    secondary: { main: GREEN, contrastText: '#ffffff' },
    success: { main: GREEN },
    error: { main: RED },
    warning: { main: AMBER },
    info: { main: '#1d4e89' },
    background: { default: '#e7edf4', paper: SURFACE },
    text: { primary: TEXT, secondary: TEXT_DIM },
    divider: BORDER,
  },

  typography: {
    fontFamily: FONT_UI,
    fontSize: 13,
    h1: { fontFamily: `'Outfit', ${FONT_UI}`, fontWeight: 600, letterSpacing: '-0.02em' },
    h2: { fontFamily: `'Outfit', ${FONT_UI}`, fontWeight: 600, letterSpacing: '-0.02em' },
    h3: { fontFamily: `'Outfit', ${FONT_UI}`, fontWeight: 600, letterSpacing: '-0.02em' },
    h6: { fontFamily: `'Outfit', ${FONT_UI}`, fontWeight: 600, fontSize: '1rem' },
    subtitle2: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },

  shape: { borderRadius: 10 },

  components: {
    /* Menus, dialogs and popovers all sit on Paper — one rule themes the lot,
       including the notification dropdown in HeaderNotification.tsx. */
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 18 },
        elevation1: { boxShadow: '0 1px 2px rgba(10,28,49,.05), 0 10px 24px -16px rgba(10,28,49,.45)' },
        elevation8: { boxShadow: '0 20px 44px -22px rgba(4,20,44,.42), 0 2px 8px rgba(4,20,44,.08)' },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${BORDER}`,
          borderRadius: 18,
          boxShadow: '0 20px 44px -22px rgba(4,20,44,.42), 0 2px 8px rgba(4,20,44,.08)',
          marginTop: 8,
        },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 500,
          color: TEXT_DIM,
          minHeight: 'unset',
          padding: '9px 12px',
          '&:hover': { backgroundColor: '#eef3f9', color: TEXT },
          '&.Mui-selected': { backgroundColor: '#eef3f9', color: TEXT },
        },
      },
    },
    MuiPopover: { styleOverrides: { paper: { borderRadius: 18 } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 18 } } },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: NAVY,
          color: '#ffffff',
          fontSize: 12,
          fontWeight: 500,
          borderRadius: 8,
          padding: '7px 11px',
        },
        arrow: { color: NAVY },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 600, fontSize: 11.5 },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 600, fontSize: 13 },
      },
    },
    MuiIconButton: { styleOverrides: { root: { borderRadius: 999 } } },
    MuiBadge: {
      styleOverrides: {
        badge: { fontFamily: FONT_UI, fontWeight: 700, fontSize: 10 },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: '#edf1f6' } } },
    MuiListItemText: {
      styleOverrides: {
        primary: { fontSize: 13, fontWeight: 500 },
        secondary: { fontSize: 11.5 },
      },
    },
  },
});

export default oatTheme;
